import React from 'react';
import { useInModal } from './modalContext';

/**
 * Button component with primary and secondary variants
 * Design: Black/white minimalist, hard edges, uppercase text — except inside a Modal,
 * where it is the Portal pill (no border, round, #F2F2F2 or black) so every modal in the
 * app reads as one surface (Oct 26 2026).
 */
export default function Button({
  children,
  variant = 'primary',
  type = 'button',
  disabled = false,
  onClick,
  className = '',
  fullWidth = false,
  size = 'md',
  ...props
}) {
  const inModal = useInModal();

  const baseStyles = inModal
    ? 'inline-flex items-center justify-center rounded-full border-0 text-center font-mono font-bold uppercase leading-tight transition-[background-color,color,opacity] duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2'
    : 'font-mono font-semibold uppercase tracking-wider border-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = inModal
    ? {
      primary: 'bg-black text-white hover:opacity-85 disabled:hover:opacity-50',
      secondary: 'bg-[#F2F2F2] text-black hover:bg-black hover:text-white disabled:hover:bg-[#F2F2F2] disabled:hover:text-black',
      danger: 'bg-[#DC2626] text-white hover:bg-[#B91C1C] disabled:hover:bg-[#DC2626]',
      ghost: 'bg-transparent text-black hover:bg-[#F2F2F2] disabled:hover:bg-transparent',
    }
    : {
      primary: 'bg-black text-white border-black hover:bg-white hover:text-black disabled:hover:bg-black disabled:hover:text-white',
      secondary: 'bg-white text-black border-black hover:bg-black hover:text-white disabled:hover:bg-white disabled:hover:text-black',
      danger: 'bg-white text-red-600 border-red-600 hover:bg-red-600 hover:text-white disabled:hover:bg-white disabled:hover:text-red-600',
      ghost: 'bg-transparent text-black border-transparent hover:border-black disabled:hover:border-transparent',
    };

  const sizes = inModal
    ? {
      sm: 'min-h-[36px] px-4 py-2 text-[10px] tracking-[0.12em]',
      md: 'min-h-[46px] px-5 py-2.5 text-[11px] tracking-[0.14em]',
      lg: 'min-h-[50px] px-7 py-3 text-[12px] tracking-[0.14em]',
    }
    : {
      sm: 'px-4 py-2 text-xs',
      md: 'px-6 py-3 text-sm',
      lg: 'px-8 py-4 text-base',
    };

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
